import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  withStyles,
} from '@material-ui/core';
import styles from './styles';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { Cancel as CancelIcon, Save as SaveIcon } from '@material-ui/icons';
import {
  onCloseWithBackdropCurry,
  roundToFixed,
} from 'services/util/helperFuncs';
import { displayModes } from 'services/enums/displayModes';
import TemplateStopAddTable from './TemplateStopAddTable';
import { AddTemplateStopsToTemplateMap } from 'components';
import { TemplateStopService } from 'services/backend/templateStopService';

function AddTemplateStopsDialog(props) {
  const {
    classes,
    t,
    dialogState,
    handleCancel,
    handleClose,
    templateStops,
    templates,
    selectedTemplate,
    displayMode,
    parentMapState,
    setParentMapState,
  } = props;
  const [templateStopsToAdd, setTemplateStopsToAdd] = useState([]);
  const [templateStopsInternal, setTemplateStopsInternal] = useState([]);
  const [displayAllTemplates, setDisplayAllTemplates] = useState(true);
  const [removeAssignedStops, setRemoveAssignedStops] = useState(false);

  const [previewDuration, setPreviewDuration] = useState(null);
  const [previewLength, setPreviewLength] = useState(null);

  const [mapState, setMapState] = useState(null);

  useEffect(() => {
    setMapState(parentMapState);
  }, [parentMapState]);

  useEffect(() => {
    const templateStopIdsOnTemplates =
      displayAllTemplates || removeAssignedStops
        ? templates.map((t) => t.stops.map((s) => s.id)).flat()
        : selectedTemplate.stops.map((s) => s.id);
    const templateStopsNotOnTemplates = templateStops
      .filter(
        (templateStop) =>
          !selectedTemplate ||
          templateStop.shipperName === selectedTemplate.shipperName
      ) // Filter template stops for right shipper
      .filter(
        (templateStop) => !templateStopIdsOnTemplates.includes(templateStop.id)
      ); // Filter template stops for selected Template
    setTemplateStopsInternal(templateStopsNotOnTemplates);
    // eslint-disable-next-line
  }, [
    templateStops,
    selectedTemplate,
    displayAllTemplates,
    removeAssignedStops,
  ]);

  const addOrRemoveTemplateStop = (templateStop) => {
    if (templateStopsInternal.findIndex((c) => c.id === templateStop.id) >= 0) {
      const newTemplateStopsToAdd = JSON.parse(
        JSON.stringify(templateStopsToAdd)
      );
      const index = newTemplateStopsToAdd.findIndex(
        (presentTemplateStop) => presentTemplateStop.id === templateStop.id
      );
      if (index >= 0) {
        newTemplateStopsToAdd.splice(index, 1);
      } else {
        newTemplateStopsToAdd.push(templateStop);
      }
      setTemplateStopsToAdd(newTemplateStopsToAdd);

      if (selectedTemplate) {
        TemplateStopService.getTemplatePreviewDuration(
          selectedTemplate.id,
          newTemplateStopsToAdd.map((s) => s.id)
        ).then(({ duration, lengthKm }) => {
          setPreviewDuration(duration);
          setPreviewLength(lengthKm);
        });
      }
    }
  };

  const handleOk = () => {
    setParentMapState(mapState);
    handleClose(templateStopsToAdd, selectedTemplate);
    setTemplateStopsToAdd([]);
  };

  const handleNotOk = () => {
    handleCancel();
    setTemplateStopsToAdd([]);
  };

  return (
    <Dialog
      className={classes.dialog}
      disableEscapeKeyDown
      fullWidth
      maxWidth={'xl'}
      onClose={onCloseWithBackdropCurry(handleCancel)}
      open={dialogState.open}
    >
      <div
        className={classes.headerContainer}
        style={
          selectedTemplate ? { backgroundColor: selectedTemplate.color } : {}
        }
      >
        <Typography>
          <strong>{`${t('customerAdd')} ${
            selectedTemplate
              ? `${t('toTourTemplate')} ${selectedTemplate.id}`
              : ''
          }`}</strong>
        </Typography>
        <Typography>
          <strong>{`${t('tourTemplateName')}: ${
            selectedTemplate ? selectedTemplate.templateName : '-'
          }`}</strong>
        </Typography>
        <Typography>
          <strong>{`${t('duration')}: ${roundToFixed(
            previewDuration ?? selectedTemplate?.durationMin,
            0
          )}`}</strong>
        </Typography>
        <Typography>
          <strong>{`${t('length')}: ${roundToFixed(
            previewLength ?? selectedTemplate?.lengthKm,
            2
          )}`}</strong>
        </Typography>
        <Typography>
          <strong>{`${t('tourTemplateCustomers')}: ${
            selectedTemplate?.stops
              ? selectedTemplate.stops.length + templateStopsToAdd.length
              : '-'
          }`}</strong>
        </Typography>
      </div>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item lg={7} sm={12} xl={7} xs={12}>
            <AddTemplateStopsToTemplateMap
              addedTemplateStops={templateStopsToAdd}
              className={classes.map}
              displayAllTemplates={displayAllTemplates}
              removeAssignedStops={removeAssignedStops}
              onSelectTemplateStop={addOrRemoveTemplateStop}
              selectedTemplate={selectedTemplate}
              setDisplayAllTemplates={setDisplayAllTemplates}
              setRemoveAssignedStops={setRemoveAssignedStops}
              templates={displayAllTemplates ? templates : [selectedTemplate]}
              unassignedTemplateStops={
                templateStopsInternal
                  ? templateStopsInternal.filter(
                      (templateStop) =>
                        templateStopsToAdd.findIndex(
                          (c) => c.id === templateStop.id
                        ) < 0
                    )
                  : []
              }
              mapState={mapState}
              setMapState={setMapState}
            />
          </Grid>
          <Grid item lg={5} sm={12} xl={5} xs={12}>
            <TemplateStopAddTable
              addOrRemoveTemplateStop={addOrRemoveTemplateStop}
              className={classes.table}
              displayMode={displayMode}
              selectedTemplate={selectedTemplate}
              templateStops={templateStopsInternal}
              templateStopsToAdd={templateStopsToAdd}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="default" onClick={handleNotOk}>
          <CancelIcon />
          &nbsp;{t('dialogCancel')}
        </Button>
        <Button
          color="primary"
          disabled={!templateStopsToAdd || templateStopsToAdd.length === 0}
          onClick={handleOk}
        >
          <SaveIcon />
          &nbsp;{t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddTemplateStopsDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  dialogState: PropTypes.object.isRequired,
  displayMode: PropTypes.oneOf(Object.values(displayModes)).isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.object,
  t: PropTypes.func.isRequired,
  templateStops: PropTypes.array.isRequired,
  templates: PropTypes.array.isRequired,
  parentMapState: PropTypes.object.isRequired,
  setParentMapState: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  withTranslation()
)(AddTemplateStopsDialog);
